'use client';

import { trackEvent } from '@/lib/track';

/**
 * The one place a free tool reports that a visitor actually FINISHED something.
 *
 * WHY THIS EXISTS. As of September 2026, 97 of ~100 free tools emit no
 * completion event at all (DEV-15115, umbrella DEV-15117). Every tool reports
 * pageviews and signpost clicks, so we can see arrival and exit but not the
 * only moment that says the tool worked. A page with 90,000 clicks and no
 * completion signal cannot be judged against one with 200.
 *
 * The cheapest possible coverage is here rather than in each tool: the three
 * shared mockup renderers (`chatMockup`, `postMockup`, `aiMockup`) sit behind 13
 * tools between them, so wiring this in three places reports completion for all
 * of them without touching a single page file.
 *
 * ⚠️ THE SLUG IS READ FROM THE URL, NOT PASSED IN. That is deliberate: passing
 * it would have meant editing ten live tool pages to add a prop that is already
 * sitting in `location.pathname`, and each of those edits is a chance to break a
 * page earning real traffic. The last path segment IS the tool slug for every
 * free tool on the site.
 *
 * ⚠️ NOTHING FIRES OUTSIDE PRODUCTION. `trackEvent` drops any event not sent
 * from www.commoninja.com, so this cannot be verified on localhost or on a
 * preview deploy. Check it in Mixpanel after the change is live, not before.
 *
 * `result` is for calculators and analyzers, where the finished moment is a
 * successful Calculate/Analyze click rather than a file or a copy.
 *
 * `extra` is optional and additive, e.g. `{ layout: EDITOR_LAYOUT_VERSION }`
 * from tools on the shared editor, so completions split by layout and not
 * only by date. `tool` and `output` always win.
 */
export const trackToolCompleted = (
  output: 'png' | 'zip' | 'copy' | 'file' | 'result' = 'png',
  extra?: Record<string, string | number | boolean>
) => {
  try {
    if (typeof window === 'undefined') return;
    const segments = window.location.pathname.split('/').filter(Boolean);
    const slug = segments[segments.length - 1] || 'unknown';
    trackEvent('tool_completed', { ...extra, tool: slug, output });
  } catch (err) {
    /* analytics must never break the thing the visitor actually came for */
  }
};
